import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { salesAPI, watchAPI, clientAPI, invoicesAPI } from '../services/api';
import { ArrowLeft, Save, DollarSign, TrendingUp, Plus, X, Users, Building2 } from 'lucide-react';

// Tax rates by jurisdiction (state/city)
const TAX_RATES = {
  'CA': { label: 'California', rate: 7.25 },
  'CA_LA': { label: 'California - Los Angeles', rate: 9.5 },
  'CA_SF': { label: 'California - San Francisco', rate: 8.625 },
  'NY': { label: 'New York', rate: 4.0 },
  'NY_NYC': { label: 'New York - NYC', rate: 8.875 },
  'TX': { label: 'Texas', rate: 6.25 },
  'TX_HOUSTON': { label: 'Texas - Houston', rate: 8.25 },
  'FL': { label: 'Florida', rate: 6.0 },
  'FL_MIAMI': { label: 'Florida - Miami', rate: 7.0 },
  'NV': { label: 'Nevada', rate: 6.85 },
  'NV_LAS_VEGAS': { label: 'Nevada - Las Vegas', rate: 8.375 },
  'IL': { label: 'Illinois', rate: 6.25 },
  'IL_CHICAGO': { label: 'Illinois - Chicago', rate: 10.25 },
  'PA': { label: 'Pennsylvania', rate: 6.0 },
  'PA_PHILLY': { label: 'Pennsylvania - Philadelphia', rate: 8.0 },
  'WA': { label: 'Washington', rate: 6.5 },
  'WA_SEATTLE': { label: 'Washington - Seattle', rate: 10.25 },
  'MA': { label: 'Massachusetts', rate: 6.25 },
  'GA': { label: 'Georgia', rate: 4.0 },
  'GA_ATLANTA': { label: 'Georgia - Atlanta', rate: 8.9 },
  'AZ': { label: 'Arizona', rate: 5.6 },
  'CO': { label: 'Colorado', rate: 2.9 },
  'CO_DENVER': { label: 'Colorado - Denver', rate: 8.81 },
  'OR': { label: 'Oregon', rate: 0.0 },
  'NH': { label: 'New Hampshire', rate: 0.0 },
  'DE': { label: 'Delaware', rate: 0.0 },
  'MT': { label: 'Montana', rate: 0.0 },
  'AK': { label: 'Alaska', rate: 0.0 },
};

const RecordSalePage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [watches, setWatches] = useState([]);
  const [clients, setClients] = useState([]);
  const [fetchError, setFetchError] = useState(null);
  const [submitError, setSubmitError] = useState(null);
  const [formData, setFormData] = useState({
    watch_id: '',
    client_id: '',
    sale_price: '',
    payment_method: 'Wire',
    payment_terms: '',
    sale_date: new Date().toISOString().split('T')[0],
    notes: '',
    discount: '',
    sale_location: '', // Location/jurisdiction for tax calculation
    tax_rate: '',
    shipping_notes: '',
    // Ship-to address fields
    ship_to_name: '',
    ship_to_address: '',
    ship_to_city: '',
    ship_to_state: '',
    ship_to_zip_code: '',
    // Payment information fields
    zelle_email: '',
    account_name: '',
    account_address: '',
    account_number: '',
    routing_number: '',
  });

  const [selectedWatch, setSelectedWatch] = useState(null);
  const [profitPreview, setProfitPreview] = useState(null);
  const [calculatedTax, setCalculatedTax] = useState(0);

  // New client modal state
  const [showNewClientModal, setShowNewClientModal] = useState(false);
  const [creatingClient, setCreatingClient] = useState(false);
  const [clientError, setClientError] = useState(null);
  const [newClientData, setNewClientData] = useState({
    client_type: 'Customer',
    name: '',
    email: '',
    phone: '',
    company: '',
  });

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (formData.watch_id && formData.sale_price) {
      const watch = watches.find(w => w.id === parseInt(formData.watch_id));
      if (watch) {
        const profit = parseFloat(formData.sale_price) - parseFloat(watch.adjusted_cost_basis || 0);
        const margin = (profit / parseFloat(formData.sale_price)) * 100;
        setProfitPreview({ profit, margin });
      }
    } else {
      setProfitPreview(null);
    }
  }, [formData.watch_id, formData.sale_price, watches]);

  // Calculate tax automatically when sale_price or tax_rate changes
  useEffect(() => {
    if (formData.sale_price && formData.tax_rate) {
      const salePrice = parseFloat(formData.sale_price);
      const taxRate = parseFloat(formData.tax_rate);
      const taxAmount = (salePrice * taxRate) / 100;
      setCalculatedTax(taxAmount);
    } else {
      setCalculatedTax(0);
    }
  }, [formData.sale_price, formData.tax_rate]);

  const fetchData = async () => {
    try {
      setFetchError(null);
      const [watchesRes, clientsRes] = await Promise.all([
        watchAPI.getAll(),
        clientAPI.getAll()
      ]);

      // Filter only available watches
      const availableWatches = (watchesRes.data.watches || []).filter(
        w => w.status === 'Available'
      );
      setWatches(availableWatches);
      setClients(clientsRes.data.clients || []);
    } catch (error) {
      console.error('Error fetching data:', error);
      setFetchError('Failed to load watches and clients. Please refresh the page.');
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    // Handle location selection - auto-set tax rate
    if (name === 'sale_location') {
      const location = TAX_RATES[value];
      setFormData(prev => ({
        ...prev,
        [name]: value,
        tax_rate: location ? location.rate.toString() : ''
      }));
      return;
    }

    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    if (name === 'watch_id') {
      const watch = watches.find(w => w.id === parseInt(value));
      setSelectedWatch(watch);
      if (watch) {
        setFormData(prev => ({
          ...prev,
          sale_price: watch.asking_price || ''
        }));
      }
    }
  };

  const handleNewClientChange = (e) => {
    const { name, value } = e.target;
    setNewClientData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleCreateClient = async (e) => {
    e.preventDefault();
    setCreatingClient(true);
    setClientError(null);

    try {
      const response = await clientAPI.create(newClientData);
      const newClient = response.data;

      // Add new client to the list
      setClients(prev => [newClient, ...prev]);

      // Auto-select the new client
      setFormData(prev => ({
        ...prev,
        client_id: newClient.id.toString()
      }));

      // Reset and close modal
      setNewClientData({
        client_type: 'Customer',
        name: '',
        email: '',
        phone: '',
        company: '',
      });
      setClientError(null);
      setShowNewClientModal(false);
    } catch (error) {
      console.error('Error creating client:', error);
      setClientError(error.response?.data?.detail || 'Failed to create client. Please try again.');
    } finally {
      setCreatingClient(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setSubmitError(null);

    try {
      // Build comprehensive notes with all additional info
      let combinedNotes = formData.notes || '';

      if (formData.sale_location) {
        const location = TAX_RATES[formData.sale_location];
        combinedNotes += `${combinedNotes ? '\n\n' : ''}Sale Location: ${location.label}`;
      }
      if (formData.discount) {
        combinedNotes += `${combinedNotes ? '\n' : ''}Discount: $${formData.discount}`;
      }
      if (calculatedTax > 0) {
        combinedNotes += `${combinedNotes ? '\n' : ''}Tax: $${calculatedTax.toFixed(2)} (${formData.tax_rate}%)`;
      }
      if (formData.payment_terms) {
        combinedNotes += `${combinedNotes ? '\n' : ''}Payment Terms: ${formData.payment_terms}`;
      }
      if (formData.shipping_notes) {
        combinedNotes += `${combinedNotes ? '\n\n' : ''}Shipping/Delivery:\n${formData.shipping_notes}`;
      }

      const submitData = {
        watch_id: parseInt(formData.watch_id),
        client_id: formData.client_id ? parseInt(formData.client_id) : null,
        sale_price: parseFloat(formData.sale_price),
        payment_method: formData.payment_method,
        sale_date: formData.sale_date ? new Date(formData.sale_date).toISOString() : null,
        notes: combinedNotes || null
      };

      const saleResponse = await salesAPI.create(submitData);
      const sale = saleResponse.data;

      // Create invoice automatically after sale
      try {
        const watch = watches.find(w => w.id === parseInt(formData.watch_id));
        const client = clients.find(c => c.id === parseInt(formData.client_id));

        const taxAmount = calculatedTax;
        const subtotal = parseFloat(formData.sale_price);
        const taxRate = parseFloat(formData.tax_rate || 0);

        // Build line items for the invoice
        const lineItems = [
          {
            description: `${watch.brand} ${watch.model}${watch.reference ? ` (Ref: ${watch.reference})` : ''}`,
            quantity: 1,
            unit_price: subtotal,
            total: subtotal
          }
        ];

        // Add discount as line item if applicable
        if (formData.discount && parseFloat(formData.discount) > 0) {
          lineItems.push({
            description: 'Discount',
            quantity: 1,
            unit_price: -parseFloat(formData.discount),
            total: -parseFloat(formData.discount)
          });
        }

        const invoiceData = {
          invoice_type: 'Sale',
          sale_id: sale.id,
          client_id: formData.client_id ? parseInt(formData.client_id) : null,
          watch_id: parseInt(formData.watch_id),
          subtotal: subtotal,
          tax_rate: taxRate,
          tax_amount: taxAmount,
          payment_method: formData.payment_method,
          payment_terms: formData.payment_terms || 'Due on Receipt',
          due_days: 0,
          line_items: lineItems,
          notes: formData.notes || null,
          // Ship-to fields
          ship_to_name: formData.ship_to_name || null,
          ship_to_address: formData.ship_to_address || null,
          ship_to_city: formData.ship_to_city || null,
          ship_to_state: formData.ship_to_state || null,
          ship_to_zip_code: formData.ship_to_zip_code || null,
          // Payment info fields
          zelle_email: formData.payment_method === 'Zelle' ? formData.zelle_email || null : null,
          account_name: formData.payment_method === 'Wire' ? formData.account_name || null : null,
          account_address: formData.payment_method === 'Wire' ? formData.account_address || null : null,
          account_number: formData.payment_method === 'Wire' ? formData.account_number || null : null,
          routing_number: formData.payment_method === 'Wire' ? formData.routing_number || null : null,
        };

        const invoiceResponse = await invoicesAPI.create(invoiceData);

        // Generate PDF for the invoice
        await invoicesAPI.generatePDF(invoiceResponse.data.id);
      } catch (invoiceError) {
        console.error('Error creating invoice:', invoiceError);
        // Continue to sale page even if invoice creation fails
        setSubmitError('Sale recorded successfully, but there was an issue creating the invoice. You can create it manually later.');
      }

      // Only navigate if there's no submit error
      if (!submitError) {
        navigate(`/sales/${sale.id}`);
      }
    } catch (error) {
      console.error('Error recording sale:', error);
      setSubmitError(error.response?.data?.detail || 'Failed to record sale. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <button
          onClick={() => navigate('/sales')}
          className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-xl transition-all"
        >
          <ArrowLeft className="w-5 h-5" />
          Back to Sales
        </button>
      </div>

      <div className="bg-white rounded-3xl shadow-xl p-8 border-2 border-gray-100">
        <h1 className="text-3xl font-bold text-gray-900 mb-6">Record New Sale</h1>

        {fetchError && (
          <div className="mb-6 p-4 bg-red-50 border-2 border-red-200 rounded-xl text-red-800">
            <p className="font-medium">{fetchError}</p>
          </div>
        )}

        {submitError && (
          <div className="mb-6 p-4 bg-yellow-50 border-2 border-yellow-200 rounded-xl text-yellow-800">
            <p className="font-medium">{submitError}</p>
          </div>
        )}

        {watches.length === 0 && !fetchError && (
          <div className="mb-6 p-4 bg-yellow-50 border-2 border-yellow-200 rounded-xl text-yellow-800">
            <p className="font-medium">No available watches in inventory</p>
            <p className="text-sm mt-1">Add watches to your inventory before recording a sale.</p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Watch Selection */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Select Watch *
            </label>
            <select
              name="watch_id"
              value={formData.watch_id}
              onChange={handleChange}
              required
              className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-slate-500 focus:outline-none"
            >
              <option value="">Choose a watch...</option>
              {watches.map((watch) => (
                <option key={watch.id} value={watch.id}>
                  {watch.brand} {watch.model} {watch.reference ? `(${watch.reference})` : ''} - $
                  {parseFloat(watch.asking_price || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </option>
              ))}
            </select>
          </div>

          {/* Selected Watch Preview */}
          {selectedWatch && (
            <div className="p-6 bg-slate-50 border-2 border-slate-100 rounded-2xl">
              <h3 className="font-bold text-gray-900 mb-3">Selected Watch Details</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                <div>
                  <div className="text-gray-500">Brand</div>
                  <div className="font-bold text-gray-900">{selectedWatch.brand}</div>
                </div>
                <div>
                  <div className="text-gray-500">Model</div>
                  <div className="font-bold text-gray-900">{selectedWatch.model}</div>
                </div>
                <div>
                  <div className="text-gray-500">Cost</div>
                  <div className="font-bold text-gray-900">
                    ${parseFloat(selectedWatch.cost || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </div>
                </div>
                <div>
                  <div className="text-gray-500">Asking Price</div>
                  <div className="font-bold text-gray-900">
                    ${parseFloat(selectedWatch.asking_price || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Sale Price and Related Fields */}
          <div className="space-y-4 p-6 bg-emerald-50 border-2 border-emerald-100 rounded-2xl">
            <h3 className="font-bold text-gray-900">Pricing Details</h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Sale Price */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Final Sale Price ($) *
                </label>
                <input
                  type="number"
                  name="sale_price"
                  value={formData.sale_price}
                  onChange={handleChange}
                  required
                  step="0.01"
                  min="0"
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-slate-500 focus:outline-none"
                  placeholder="10000.00"
                />
              </div>

              {/* Discount */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Discount Given ($)
                </label>
                <input
                  type="number"
                  name="discount"
                  value={formData.discount}
                  onChange={handleChange}
                  step="0.01"
                  min="0"
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-slate-500 focus:outline-none"
                  placeholder="0.00"
                />
              </div>

              {/* Sale Location for Tax Calculation */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Sale Location (for tax rate) *
                </label>
                <select
                  name="sale_location"
                  value={formData.sale_location}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-slate-500 focus:outline-none"
                >
                  <option value="">Select location where sale occurs...</option>
                  {Object.entries(TAX_RATES)
                    .sort((a, b) => a[1].label.localeCompare(b[1].label))
                    .map(([key, value]) => (
                      <option key={key} value={key}>
                        {value.label} ({value.rate}%)
                      </option>
                    ))}
                </select>
                <p className="mt-1 text-xs text-gray-500">
                  Select the state/city where this sale is being conducted for accurate tax calculation
                </p>
              </div>

              {/* Tax Rate & Amount Display */}
              {formData.sale_location && (
                <div className="p-4 bg-white rounded-xl border-2 border-blue-200">
                  <div className="text-sm font-semibold text-gray-600 mb-1">
                    Tax ({TAX_RATES[formData.sale_location]?.label})
                  </div>
                  <div className="text-lg font-bold text-blue-600">
                    {formData.tax_rate}% = ${calculatedTax.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </div>
                </div>
              )}

              {/* Total Calculation Display */}
              {formData.sale_price && (
                <div className="p-4 bg-white rounded-xl border-2 border-emerald-200">
                  <div className="text-sm font-semibold text-gray-600 mb-1">Total with Tax</div>
                  <div className="text-2xl font-bold text-emerald-600">
                    ${(
                      parseFloat(formData.sale_price || 0) +
                      calculatedTax
                    ).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Profit Preview */}
          {profitPreview && (
            <div className="grid grid-cols-2 gap-4">
              <div className="p-4 bg-green-50 border-2 border-green-100 rounded-xl">
                <div className="flex items-center gap-2 mb-2">
                  <DollarSign className="w-5 h-5 text-green-600" />
                  <span className="text-sm font-semibold text-gray-700">Profit</span>
                </div>
                <div className={`text-2xl font-bold ${
                  profitPreview.profit > 0 ? 'text-green-600' : 'text-red-600'
                }`}>
                  ${profitPreview.profit.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </div>
              </div>
              <div className="p-4 bg-emerald-50 border-2 border-emerald-100 rounded-xl">
                <div className="flex items-center gap-2 mb-2">
                  <TrendingUp className="w-5 h-5 text-emerald-600" />
                  <span className="text-sm font-semibold text-gray-700">Margin</span>
                </div>
                <div className={`text-2xl font-bold ${
                  profitPreview.margin > 10 ? 'text-emerald-600' : 'text-yellow-600'
                }`}>
                  {profitPreview.margin.toFixed(1)}%
                </div>
              </div>
            </div>
          )}

          {/* Client Selection */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-semibold text-gray-700">
                Client (Optional)
              </label>
              <button
                type="button"
                onClick={() => setShowNewClientModal(true)}
                className="flex items-center gap-1 px-3 py-1.5 text-sm bg-slate-50 text-slate-900 rounded-lg hover:bg-slate-100 transition-all font-medium"
              >
                <Plus className="w-4 h-4" />
                New Client
              </button>
            </div>
            <select
              name="client_id"
              value={formData.client_id}
              onChange={handleChange}
              className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-slate-500 focus:outline-none"
            >
              <option value="">Walk-in customer (no client record)</option>
              {clients.map((client) => (
                <option key={client.id} value={client.id}>
                  {client.name} {client.email ? `(${client.email})` : ''}
                </option>
              ))}
            </select>
          </div>

          {/* Payment Method and Terms */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Payment Method
              </label>
              <select
                name="payment_method"
                value={formData.payment_method}
                onChange={handleChange}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-slate-500 focus:outline-none"
              >
                <option value="Wire">Wire</option>
                <option value="Check">Check</option>
                <option value="Credit Card">Credit Card</option>
                <option value="Debit Card">Debit Card</option>
                <option value="Zelle">Zelle</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Payment Terms / Status
              </label>
              <input
                type="text"
                name="payment_terms"
                value={formData.payment_terms || ''}
                onChange={handleChange}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-slate-500 focus:outline-none"
                placeholder="e.g., Net 30, COD, Paid in Full"
              />
            </div>
          </div>

          {/* Conditional Payment Information Fields */}
          {formData.payment_method === 'Zelle' && (
            <div className="p-6 bg-blue-50 border-2 border-blue-100 rounded-2xl">
              <h3 className="font-bold text-gray-900 mb-4">Zelle Payment Information</h3>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Zelle Email or Phone Number
                </label>
                <input
                  type="text"
                  name="zelle_email"
                  value={formData.zelle_email}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-slate-500 focus:outline-none"
                  placeholder="email@example.com or +1 (555) 123-4567"
                />
              </div>
            </div>
          )}

          {formData.payment_method === 'Wire' && (
            <div className="p-6 bg-blue-50 border-2 border-blue-100 rounded-2xl">
              <h3 className="font-bold text-gray-900 mb-4">Wire Transfer Information</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Account Name
                  </label>
                  <input
                    type="text"
                    name="account_name"
                    value={formData.account_name}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-slate-500 focus:outline-none"
                    placeholder="Business Account Name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Account Address
                  </label>
                  <input
                    type="text"
                    name="account_address"
                    value={formData.account_address}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-slate-500 focus:outline-none"
                    placeholder="123 Bank Street, City, ST 12345"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Account Number
                  </label>
                  <input
                    type="text"
                    name="account_number"
                    value={formData.account_number}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-slate-500 focus:outline-none"
                    placeholder="123456789"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Routing Number
                  </label>
                  <input
                    type="text"
                    name="routing_number"
                    value={formData.routing_number}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-slate-500 focus:outline-none"
                    placeholder="021000021"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Ship To Address */}
          <div className="p-6 bg-purple-50 border-2 border-purple-100 rounded-2xl">
            <h3 className="font-bold text-gray-900 mb-4">Ship To Address (Optional)</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Recipient Name
                </label>
                <input
                  type="text"
                  name="ship_to_name"
                  value={formData.ship_to_name}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-slate-500 focus:outline-none"
                  placeholder="John Doe"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Street Address
                </label>
                <input
                  type="text"
                  name="ship_to_address"
                  value={formData.ship_to_address}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-slate-500 focus:outline-none"
                  placeholder="123 Main Street"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  City
                </label>
                <input
                  type="text"
                  name="ship_to_city"
                  value={formData.ship_to_city}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-slate-500 focus:outline-none"
                  placeholder="New York"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  State
                </label>
                <input
                  type="text"
                  name="ship_to_state"
                  value={formData.ship_to_state}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-slate-500 focus:outline-none"
                  placeholder="NY"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Zip Code
                </label>
                <input
                  type="text"
                  name="ship_to_zip_code"
                  value={formData.ship_to_zip_code}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-slate-500 focus:outline-none"
                  placeholder="10001"
                />
              </div>
            </div>
          </div>

          {/* Sale Date */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Sale Date
            </label>
            <input
              type="date"
              name="sale_date"
              value={formData.sale_date}
              onChange={handleChange}
              className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-slate-500 focus:outline-none"
            />
          </div>

          {/* Notes and Shipping */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Sale Notes
              </label>
              <textarea
                name="notes"
                value={formData.notes}
                onChange={handleChange}
                rows="3"
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-slate-500 focus:outline-none resize-none"
                placeholder="Additional notes about this sale..."
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Shipping / Delivery Notes
              </label>
              <textarea
                name="shipping_notes"
                value={formData.shipping_notes}
                onChange={handleChange}
                rows="3"
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-slate-500 focus:outline-none resize-none"
                placeholder="Shipping address, delivery instructions, tracking info..."
              />
            </div>
          </div>

          {/* Submit Button */}
          <div className="flex justify-end gap-4">
            <button
              type="button"
              onClick={() => navigate('/sales')}
              className="px-6 py-3 bg-gray-200 text-gray-700 rounded-2xl font-semibold hover:bg-gray-300 transition-all"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || !formData.watch_id || !formData.sale_price || !formData.sale_location}
              className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-emerald-600 to-green-600 text-white rounded-2xl font-semibold shadow-lg hover:from-emerald-700 hover:to-green-700 transition-all disabled:opacity-50"
            >
              <Save className="w-5 h-5" />
              {loading ? 'Recording...' : 'Record Sale'}
            </button>
          </div>
        </form>
      </div>

      {/* New Client Modal */}
      {showNewClientModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-3xl shadow-2xl max-w-md w-full p-8 border-2 border-gray-100">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-gray-900">Add New Client</h2>
              <button
                onClick={() => setShowNewClientModal(false)}
                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-xl transition-all"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleCreateClient} className="space-y-4">
              {clientError && (
                <div className="p-3 bg-red-50 border-2 border-red-200 rounded-xl text-red-800 text-sm">
                  {clientError}
                </div>
              )}

              {/* Client Type Selection */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Client Type *
                </label>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    type="button"
                    onClick={() => setNewClientData(prev => ({ ...prev, client_type: 'Customer' }))}
                    className={`p-4 rounded-xl border-2 transition-all ${
                      newClientData.client_type === 'Customer'
                        ? 'border-emerald-500 bg-emerald-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <Users className={`w-6 h-6 mx-auto mb-1 ${
                      newClientData.client_type === 'Customer' ? 'text-emerald-600' : 'text-gray-400'
                    }`} />
                    <div className="text-sm font-bold text-gray-900">Customer</div>
                  </button>

                  <button
                    type="button"
                    onClick={() => setNewClientData(prev => ({ ...prev, client_type: 'Dealer' }))}
                    className={`p-4 rounded-xl border-2 transition-all ${
                      newClientData.client_type === 'Dealer'
                        ? 'border-amber-500 bg-amber-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <Building2 className={`w-6 h-6 mx-auto mb-1 ${
                      newClientData.client_type === 'Dealer' ? 'text-amber-600' : 'text-gray-400'
                    }`} />
                    <div className="text-sm font-bold text-gray-900">Dealer</div>
                  </button>
                </div>
              </div>

              {/* Name */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Full Name *
                </label>
                <input
                  type="text"
                  name="name"
                  value={newClientData.name}
                  onChange={handleNewClientChange}
                  required
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-slate-500 focus:outline-none"
                  placeholder={newClientData.client_type === 'Dealer' ? 'Contact Person Name' : 'John Doe'}
                />
              </div>

              {/* Company/Shop Name */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  {newClientData.client_type === 'Dealer' ? 'Shop/Business Name' : 'Company'} {newClientData.client_type === 'Dealer' && '*'}
                </label>
                <input
                  type="text"
                  name="company"
                  value={newClientData.company}
                  onChange={handleNewClientChange}
                  required={newClientData.client_type === 'Dealer'}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-slate-500 focus:outline-none"
                  placeholder={newClientData.client_type === 'Dealer' ? 'Luxury Watches LLC' : 'Company name (optional)'}
                />
              </div>

              {/* Email */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Email
                </label>
                <input
                  type="email"
                  name="email"
                  value={newClientData.email}
                  onChange={handleNewClientChange}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-slate-500 focus:outline-none"
                  placeholder="contact@example.com"
                />
              </div>

              {/* Phone */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Phone
                </label>
                <input
                  type="tel"
                  name="phone"
                  value={newClientData.phone}
                  onChange={handleNewClientChange}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-slate-500 focus:outline-none"
                  placeholder="+1 (555) 123-4567"
                />
              </div>

              {/* Buttons */}
              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowNewClientModal(false)}
                  className="flex-1 px-4 py-3 bg-gray-200 text-gray-700 rounded-xl font-semibold hover:bg-gray-300 transition-all"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={
                    creatingClient ||
                    !newClientData.name ||
                    (newClientData.client_type === 'Dealer' && !newClientData.company)
                  }
                  className="flex-1 px-4 py-3 bg-gradient-to-r from-slate-900 to-slate-700 text-white rounded-xl font-semibold shadow-lg hover:from-slate-950 hover:to-slate-800 transition-all disabled:opacity-50"
                >
                  {creatingClient ? 'Creating...' : 'Create Client'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default RecordSalePage;
